import { useCallback, useEffect, useMemo, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useAuth } from "../../core/auth/AuthContext";
import {
  getPublicUsername,
  resolvePublicIdentityLabel,
} from "../../utils/publicIdentity";
import EventGlyph from "../icons/EventGlyph";
import EventListItem from "./EventListItem";
import EventSpotlight, { EVENT_SPOTLIGHT_FRAME_HEIGHT } from "./EventSpotlight";
import { selectNewOrganizerEvents } from "../../features/events/eventSpotlight";
import { resolveEventListScrollElement } from "./eventListScrollElement";
import styles from "./EventList.module.css";

const DAY_ROW_SIZE = 40;
const EVENT_CARD_SIZE_DESKTOP = 132;
const EVENT_CARD_SIZE_MOBILE = 118;
const EVENT_ROW_GAP_MOBILE = 4;
const EVENT_ROW_GAP_DESKTOP = 8;
const EVENT_ROW_SIZE_MOBILE = EVENT_CARD_SIZE_MOBILE + EVENT_ROW_GAP_MOBILE;
const EVENT_ROW_SIZE_DESKTOP = EVENT_CARD_SIZE_DESKTOP + EVENT_ROW_GAP_DESKTOP;

function parseEventStartTimestamp(event) {
  if (!event?.date) {
    return Number.POSITIVE_INFINITY;
  }

  const [year = 0, month = 1, day = 1] = String(event.date)
    .split("-")
    .map((value) => Number.parseInt(value, 10));
  const [hours = 23, minutes = 59] = String(event.startTime || "23:59")
    .split(":")
    .map((value) => Number.parseInt(value, 10));

  return new Date(year, month - 1, day, hours, minutes).getTime();
}

function formatDayLabel(dateValue) {
  if (!dateValue) {
    return "Date à confirmer";
  }

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return "Date à confirmer";
  }

  const formatted = date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return toTitleCaseWords(formatted);
}

function toTitleCaseWords(value) {
  return String(value || "").replace(/\b([a-z])/g, (letter) => letter.toUpperCase());
}

export function buildEventListRows(events, orderMode = "chronological") {
  if (orderMode === "ranked") {
    return events.map((event) => ({
      id: event.id,
      type: "event",
      event,
    }));
  }

  const sortedEvents = [...events].sort((leftEvent, rightEvent) => {
    const leftDay = leftEvent.date || "";
    const rightDay = rightEvent.date || "";

    if (leftDay !== rightDay) {
      return leftDay.localeCompare(rightDay);
    }

    const leftDate = parseEventStartTimestamp(leftEvent);
    const rightDate = parseEventStartTimestamp(rightEvent);
    if (leftDate !== rightDate) return leftDate - rightDate;
    return String(leftEvent.id || "").localeCompare(String(rightEvent.id || ""));
  });

  const rows = [];
  const eventsCountByDay = sortedEvents.reduce((accumulator, event) => {
    const dayKey = event.date || "unknown-date";
    accumulator[dayKey] = (accumulator[dayKey] || 0) + 1;
    return accumulator;
  }, {});
  let previousDayKey = null;

  sortedEvents.forEach((event) => {
    const dayKey = event.date || "unknown-date";

    if (dayKey !== previousDayKey) {
      rows.push({
        id: `day-${dayKey}`,
        type: "day",
        label: formatDayLabel(event.date),
        count: eventsCountByDay[dayKey],
      });
      previousDayKey = dayKey;
    }

    rows.push({
      id: event.id,
      type: "event",
      event,
    });
  });

  return rows;
}

function buildRowMetrics(rows, eventRowSize, spotlightRowSize) {
  let offset = 0;

  return rows.map((row) => {
    const size = row.type === "day" ? DAY_ROW_SIZE : row.type === "spotlight" ? spotlightRowSize : eventRowSize;
    const metric = {
      id: row.id,
      type: row.type,
      label: row.type === "day" ? row.label : null,
      count: row.type === "day" ? row.count : null,
      start: offset,
      size,
    };
    offset += size;
    return metric;
  });
}

function getActiveDay(dayMetrics, scrollTop) {
  if (!dayMetrics.length) return null;

  const threshold = Math.max(0, Number(scrollTop) || 0) + 12;
  if (threshold < dayMetrics[0].start) return null;
  let low = 0;
  let high = dayMetrics.length - 1;
  let activeIndex = -1;

  // Day rows are already ordered by their virtual start offset. A binary
  // search keeps the floating day indicator cheap even when the list grows
  // to several hundred events.
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    if (dayMetrics[middle].start <= threshold) {
      activeIndex = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return dayMetrics[activeIndex >= 0 ? activeIndex : 0] || null;
}

function isSecretEvent(event) {
  return String(event?.visibility || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_") === "link_only";
}

function buildEventContextBadges(event, { showSecretContextBadges = true } = {}) {
  return showSecretContextBadges && isSecretEvent(event)
    ? [{ label: "Secret", tone: "secret" }]
    : [];
}

export default function EventList({
  onSelectEvent,
  mobile = false,
  events: eventsOverride,
  distanceOriginCenter = null,
  selectedEventId = null,
  scrollContainerSelector = "[data-panel-scroll]",
  scrollElementRef = null,
  interestsByEventId = {},
  favoritesByEventId = {},
  onToggleInterest,
  isInterestPending,
  onToggleFavorite,
  showSecretContextBadges = true,
  emptyTitle = "Aucun evenement visible",
  emptyBody = "Déplace la carte ou ajuste ton point de vue pour faire apparaître d'autres sorties.",
  emptyAction = null,
  orderMode = "chronological",
  readOnly = false,
  showNewOrganizers = false,
  spotlightTitle = "À la une",
  // Contenu facultatif sous la derniere sortie (hors virtualisation).
  footer = null,
}) {
  const { user: authUser } = useAuth();

  const events = Array.isArray(eventsOverride) ? eventsOverride : [];
  const spotlightEvents = useMemo(
    () => showNewOrganizers ? selectNewOrganizerEvents(events) : [],
    [events, showNewOrganizers]
  );
  const hasSpotlight = spotlightEvents.length > 0;
  const rows = useMemo(
    () => [
      ...(spotlightEvents.length ? [{ id: "spotlight-new-organizers", type: "spotlight" }] : []),
      ...buildEventListRows(events, orderMode),
    ],
    [events, orderMode, spotlightEvents]
  );
  const eventRowSize = mobile ? EVENT_ROW_SIZE_MOBILE : EVENT_ROW_SIZE_DESKTOP;
  const spotlightRowSize = (mobile ? EVENT_CARD_SIZE_MOBILE : EVENT_CARD_SIZE_DESKTOP) + EVENT_SPOTLIGHT_FRAME_HEIGHT;
  const rowMetrics = useMemo(() => buildRowMetrics(rows, eventRowSize, spotlightRowSize), [rows, eventRowSize, spotlightRowSize]);
  const dayMetrics = useMemo(
    () => rowMetrics.filter((metric) => metric.type === "day"),
    [rowMetrics]
  );
  const [activeDay, setActiveDay] = useState(() => getActiveDay(dayMetrics, 0));
  const [isScrollDayVisible, setIsScrollDayVisible] = useState(false);
  const getScrollElement = useCallback(
    () => resolveEventListScrollElement(scrollElementRef, scrollContainerSelector),
    [scrollElementRef, scrollContainerSelector]
  );
  const currentUserPreview = useMemo(() => {
    if (!authUser) {
      return null;
    }

    const userId = authUser.id || authUser.uid;

    if (!userId) {
      return null;
    }

    return {
      id: userId,
      uid: userId,
      displayName: resolvePublicIdentityLabel(authUser, "Moi"),
      username: getPublicUsername(authUser) || null,
      photoURL: authUser.photoURL || null,
      avatarUrl: authUser.photoURL || null,
      media: {
        photos: Array.isArray(authUser.media?.photos) ? authUser.media.photos : [],
      },
    };
  }, [authUser]);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement,
    estimateSize: (index) => rowMetrics[index]?.size || eventRowSize,
    overscan: mobile ? 6 : 5,
  });

  useEffect(() => {
    // Recalculer les offsets si le cadre ou le format de ligne change.
    rowVirtualizer.measure();
  }, [rowVirtualizer, eventRowSize, spotlightRowSize, hasSpotlight]);

  useEffect(() => {
    setActiveDay(getActiveDay(dayMetrics, 0));
  }, [dayMetrics]);

  useEffect(() => {
    if (!mobile) return undefined;

    const scrollElement = getScrollElement();
    if (!scrollElement) return undefined;
    let pendingFrame = null;
    let hideTimeout = null;

    const applyScrollState = () => {
      pendingFrame = null;
      setActiveDay(getActiveDay(dayMetrics, scrollElement.scrollTop));
      setIsScrollDayVisible(true);

      if (hideTimeout) {
        window.clearTimeout(hideTimeout);
      }

      hideTimeout = window.setTimeout(() => {
        setIsScrollDayVisible(false);
      }, 520);
    };

    const handleScroll = () => {
      // Scroll events can arrive more often than React can paint. Coalesce
      // them into one state update per frame so the virtualized list remains
      // native-scroll-first while only the small day indicator is refreshed.
      if (pendingFrame !== null) return;
      const scheduleFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
      pendingFrame = scheduleFrame(applyScrollState);
    };

    setActiveDay(getActiveDay(dayMetrics, scrollElement.scrollTop));
    scrollElement.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
      if (pendingFrame !== null) {
        if (typeof window.cancelAnimationFrame === "function") {
          window.cancelAnimationFrame(pendingFrame);
        } else {
          window.clearTimeout(pendingFrame);
        }
      }
      if (hideTimeout) {
        window.clearTimeout(hideTimeout);
      }
    };
  }, [mobile, dayMetrics, getScrollElement]);

  if (events.length === 0) {
    return (
      <div className={styles.emptyState}>
        <img src="/karango-icon.png" alt="" className={styles.emptyBgImage} aria-hidden="true" />
        <div className={styles.emptyContent}>
          <strong className={styles.emptyTitle}>{emptyTitle}</strong>
          <span className={styles.emptyBody}>{emptyBody}</span>
          {emptyAction ? <div className={styles.emptyAction}>{emptyAction}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {mobile && activeDay && (
        <div
          className={`${styles.floatingDayDock} ${
            mobile ? styles.floatingDayDockMobile : styles.floatingDayDockDesktop
          } ${isScrollDayVisible ? styles.floatingDayDockVisible : styles.floatingDayDockHidden
          }`}
        >
          <div
            className={`${styles.floatingDay} ${
              mobile ? styles.floatingDayMobile : styles.floatingDayDesktop
            }`}
          >
            <span className={styles.floatingDayLabel}>{activeDay.label}</span>
            <span className={styles.floatingDayCount}>
              <span className={styles.dayCountIcon} aria-hidden="true">
                <EventGlyph className={styles.dayCountGlyph} />
              </span>
              <span>{activeDay.count}</span>
            </span>
          </div>
        </div>
      )}

      <div
        className={styles.virtualArea}
        style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];

          return (
            <div
              key={row.id}
              className={row.type === "spotlight" ? styles.spotlightRow : row.type === "day" ? styles.dayRow : styles.eventRow}
              style={{
                transform: `translateY(${virtualRow.start}px)`,
                height: `${virtualRow.size}px`,
              }}
            >
              {row.type === "spotlight" ? (
                <EventSpotlight events={spotlightEvents} title={spotlightTitle} onSelectEvent={onSelectEvent}
                  distanceOriginCenter={distanceOriginCenter} selectedEventId={selectedEventId} readOnly={readOnly}
                  interestsByEventId={interestsByEventId}
                  onToggleInterest={readOnly ? undefined : onToggleInterest}
                  isInterestPending={isInterestPending} />
              ) : row.type === "day" ? (
                <div className={styles.dayBreak} aria-label={row.label}>
                  <span className={styles.dayLine} aria-hidden="true" />
                  <div className={styles.dayCenter}>
                    <span className={styles.dayLabel}>{row.label}</span>
                    <span className={styles.dayCount}>
                      <span className={styles.dayCountIcon} aria-hidden="true">
                        <EventGlyph className={styles.dayCountGlyph} />
                      </span>
                      <span>{row.count}</span>
                    </span>
                  </div>
                  <span className={styles.dayLine} aria-hidden="true" />
                </div>
              ) : (
                <EventListItem
                  evt={row.event}
                  distanceOriginCenter={distanceOriginCenter}
                  isSelected={selectedEventId === row.event.id}
                  isInterested={!readOnly && !!interestsByEventId[row.event.id]}
                  isInterestPending={
                    !readOnly &&
                    Boolean(isInterestPending?.(row.event.id, "INTERESTED"))
                  }
                  currentUser={currentUserPreview}
                  contextBadges={buildEventContextBadges(row.event, {
                    showSecretContextBadges,
                  })}
                  onClick={onSelectEvent}
                  // readOnly est la protection reelle (masque le bouton +
                  // ignore tout appel) ; on retire aussi le callback lui-meme
                  // en defense en profondeur, pour qu'un lien public ne
                  // puisse jamais declencher de mutation meme indirectement.
                  onToggleInterest={readOnly ? undefined : onToggleInterest}
                  readOnly={readOnly}
                />
              )}
            </div>
          );
        })}
      </div>
      {footer}
    </div>
  );
}
